import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  LinearProgress,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useKPIStore } from '../../stores/kpi.store';
import { formatTco2e } from '../../utils/carbonAvoidance.util';

interface StatCardProps {
  title: string;
  value: number | null;
  unit: string;
  trend: number;
  onMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
  /** When true, hide month-over-month trend (e.g. prior-month-only cards or overview). */
  hideTrend?: boolean;
  /** Optional leading icon (e.g. resource / category). */
  icon?: React.ReactNode;
  /** Optional second line under the main value (e.g. formatted cost). */
  subValue?: string;
  /** Period / context caption under the value (e.g. This Month, All-time). */
  subtitle?: string;
  /** Show the unit label even when value is null (displays "— unit"). */
  showUnitWhenEmpty?: boolean;
  /** Hide the overflow menu (read-only summary cards). */
  hideMenu?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  unit,
  trend,
  onMenuClick,
  hideTrend = false,
  icon,
  subValue,
  subtitle,
  showUnitWhenEmpty = false,
  hideMenu = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { limits } = useKPIStore();

  const kpiLimit =
    value !== null
      ? limits.find(
          (limit) =>
            limit.name === title.replace(/\s+/g, '_').toUpperCase() && limit.isEnabled
        )
      : undefined;

  const getProgressColor = (value: number, limit: number, operator: string) => {
    switch (operator) {
      case '>':
        return value >= limit ? theme.palette.success.main : theme.palette.error.main;
      case '<':
        return value <= limit ? theme.palette.success.main : theme.palette.error.main;
      case '>=':
        return value >= limit ? theme.palette.success.main : theme.palette.error.main;
      case '<=':
        return value <= limit ? theme.palette.success.main : theme.palette.error.main;
      case '=':
        return Math.abs(value - limit) < 0.01 ? theme.palette.success.main : theme.palette.error.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const getProgressValue = (value: number, limit: number, operator: string) => {
    switch (operator) {
      case '>':
      case '>=':
        return Math.min((value / limit) * 100, 100);
      case '<':
      case '<=':
        return Math.min((value / limit) * 100, 100);
      case '=':
        return Math.abs(value - limit) < 0.01 ? 100 : 0;
      default:
        return 0;
    }
  };

  const getProgressLabel = (value: number, limit: number, operator: string) => {
    switch (operator) {
      case '>':
        return `${value.toLocaleString()} > ${limit.toLocaleString()} ${unit}`;
      case '<':
        return `${value.toLocaleString()} < ${limit.toLocaleString()} ${unit}`;
      case '>=':
        return `${value.toLocaleString()} ≥ ${limit.toLocaleString()} ${unit}`;
      case '<=':
        return `${value.toLocaleString()} ≤ ${limit.toLocaleString()} ${unit}`;
      case '=':
        return `${value.toLocaleString()} = ${limit.toLocaleString()} ${unit}`;
      default:
        return '';
    }
  };

  const formatValue = (value: number, unit: string) => {
    if (unit === 'hours') {
      return value.toFixed(1);
    }
    if (unit === '%') {
      return value.toFixed(1);
    }
    if (unit === 'tCO2e' || unit === 'tCO₂e') {
      return formatTco2e(value);
    }
    if (unit === 'SAR') {
      return value.toLocaleString('en-US', { style: 'currency', currency: 'SAR' });
    }
    if (unit === 'SAR/kg') {
      return `${value.toLocaleString('en-US', { style: 'currency', currency: 'SAR' })}/kg`;
    }
    if (unit === 'kg/day') {
      return `${value.toLocaleString()} kg/day`;
    }
    if (unit === 'km/L') {
      return `${value.toFixed(2)} km/L`;
    }
    if (unit === 'times/day') {
      return `${value.toFixed(1)} times/day`;
    }
    return value.toLocaleString();
  };

  return (
    <Paper
      sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        bgcolor: theme.palette.background.paper,
        boxShadow: theme.shadows[2],
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, pr: 1 }}>
          {icon != null && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.primary.main,
                flexShrink: 0,
                '& .MuiSvgIcon-root': { fontSize: 28 },
              }}
            >
              {icon}
            </Box>
          )}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              lineHeight: 1.35,
              minWidth: 0,
            }}
          >
            {title}
          </Typography>
        </Box>
        {!hideMenu ? (
          <IconButton
            size="small"
            onClick={onMenuClick}
            sx={{ color: theme.palette.text.secondary }}
          >
            <MoreVertIcon />
          </IconButton>
        ) : null}
      </Box>

      {kpiLimit && value !== null && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t('dashboard.kpiTarget')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {getProgressLabel(value, kpiLimit.value, kpiLimit.operator)}
            </Typography>
          </Box>
          <Tooltip title={kpiLimit.alertMessage}>
            <LinearProgress
              variant="determinate"
              value={getProgressValue(value, kpiLimit.value, kpiLimit.operator)}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: theme.palette.action.hover,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getProgressColor(value, kpiLimit.value, kpiLimit.operator),
                },
              }}
            />
          </Tooltip>
        </Box>
      )}

      <Box sx={{ mt: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {value === null ? '—' : formatValue(value, unit)}
          </Typography>
          {(value !== null || showUnitWhenEmpty) &&
            unit !== 'SAR' &&
            unit !== 'SAR/kg' &&
            unit !== 'kg/day' &&
            unit !== 'km/L' &&
            unit !== 'times/day' && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                {unit}
              </Typography>
            )}
        </Box>
        {subValue != null && subValue !== '' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
            {subValue}
          </Typography>
        )}
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {subtitle}
          </Typography>
        ) : null}
        {!hideTrend && trend !== 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            {trend > 0 ? (
              <TrendingUpIcon sx={{ color: theme.palette.success.main }} />
            ) : (
              <TrendingDownIcon sx={{ color: theme.palette.error.main }} />
            )}
            <Typography
              variant="body2"
              sx={{
                color: trend > 0 ? theme.palette.success.main : theme.palette.error.main,
                fontWeight: 700,
                letterSpacing: 0.3,
              }}
            >
              {trend > 0 ? '+' : '-'}
              {Math.abs(trend).toFixed(1)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.vsLastMonth')}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default StatCard;
